import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Send,
  Schedule,
  Warning,
  CheckCircle,
  Notifications,
  FilterList,
  Search
} from '@mui/icons-material';
import { Reminder, Customer, Loan } from '../../models/LendingModels';
import { mockReminders, mockCustomers, mockLoans } from '../../data/MockData';
import { formatCurrency } from '../../utils/CurrencyFormat';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reminder-tabpanel-${index}`}
      aria-labelledby={`reminder-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReminderSystem: React.FC = () => {
  const [reminders, setReminders] = useState<Reminder[]>(mockReminders);
  const [customers] = useState<Customer[]>(mockCustomers);
  const [loans] = useState<Loan[]>(mockLoans);
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoReminders, setAutoReminders] = useState(true);

  const handleViewReminder = (reminder: Reminder) => {
    setSelectedReminder(reminder);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReminder(null);
  };

  const handleSendReminder = (reminderId: string) => {
    setReminders(prev => prev.map(r => 
      r.id === reminderId 
        ? { ...r, status: 'sent', sentDate: new Date() }
        : r
    ));
  };

  const handleAcknowledgeReminder = (reminderId: string) => {
    setReminders(prev => prev.map(r => 
      r.id === reminderId 
        ? { ...r, status: 'acknowledged', acknowledgedDate: new Date() }
        : r
    ));
  };

  const filteredReminders = reminders.filter(reminder => {
    const matchesSearch = reminder.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || reminder.status === statusFilter;
    const matchesType = typeFilter === 'all' || reminder.reminderType === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown';
  };

  const getLoanNumber = (loanId: string) => {
    const loan = loans.find(l => l.id === loanId);
    return loan ? loan.loanNumber : 'Unknown';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'sent': return 'info';
      case 'acknowledged': return 'success';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'payment_due': return 'warning';
      case 'overdue': return 'error';
      case 'interest_accrual': return 'info';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'payment_due': return <Schedule />;
      case 'overdue': return <Warning />;
      case 'interest_accrual': return <Notifications />;
      default: return <Notifications />;
    }
  };

  const getOverdueReminders = () => {
    const today = new Date();
    return reminders.filter(reminder => 
      reminder.status === 'pending' && 
      new Date(reminder.dueDate) < today
    );
  };

  const getUpcomingReminders = () => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return reminders.filter(reminder => 
      reminder.status === 'pending' && 
      new Date(reminder.dueDate) >= today &&
      new Date(reminder.dueDate) <= nextWeek
    );
  };

  const generateAutoReminders = () => {
    if (!autoReminders) return;
    
    const today = new Date();
    const newReminders: Reminder[] = [];
    
    // Generate payment due reminders
    loans.forEach(loan => {
      const nextPaymentDate = new Date(loan.startDate);
      nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
      
      if (nextPaymentDate > today && nextPaymentDate <= new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
        const existingReminder = reminders.find(r => 
          r.loanId === loan.id && 
          r.reminderType === 'payment_due' &&
          Math.abs(new Date(r.dueDate).getTime() - nextPaymentDate.getTime()) < 24 * 60 * 60 * 1000
        );
        
        if (!existingReminder) {
          newReminders.push({
            id: `auto-${Date.now()}-${loan.id}`,
            loanId: loan.id,
            customerId: loan.customerId,
            reminderType: 'payment_due',
            dueDate: nextPaymentDate,
            amount: 0, // Will be calculated
            status: 'pending',
            message: `Payment reminder for loan ${loan.loanNumber}`,
            createdAt: new Date(),
            updatedAt: new Date()
          });
        }
      }
    });
    
    if (newReminders.length > 0) {
      setReminders(prev => [...prev, ...newReminders]);
    }
  };

  useEffect(() => {
    generateAutoReminders();
  }, [autoReminders]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Reminder System
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoReminders}
                onChange={(e) => setAutoReminders(e.target.checked)}
              />
            }
            label="Auto Reminders"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {/* Handle add reminder */}}
          >
            Create Reminder
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Quick Stats */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Warning color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">Overdue</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {getOverdueReminders().length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Upcoming</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {getUpcomingReminders().length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Sent</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {reminders.filter(r => r.status === 'sent').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Notifications color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Total</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {reminders.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={3} lg={4}>
            <TextField
              fullWidth
              label="Search reminders"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="acknowledged">Acknowledged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="payment_due">Payment Due</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="interest_accrual">Interest Accrual</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              fullWidth
            >
              More Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Reminder List */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Loan</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReminders.map((reminder) => (
                <TableRow key={reminder.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getTypeIcon(reminder.reminderType)}
                      <Chip
                        label={reminder.reminderType.replace('_', ' ')}
                        color={getTypeColor(reminder.reminderType) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getCustomerName(reminder.customerId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getLoanNumber(reminder.loanId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300 }}>
                      {reminder.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {reminder.dueDate.toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {reminder.amount > 0 ? formatCurrency(reminder.amount) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={reminder.status}
                      color={getStatusColor(reminder.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewReminder(reminder)}
                    >
                      <Edit />
                    </IconButton>
                    {reminder.status === 'pending' && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleSendReminder(reminder.id)}
                      >
                        <Send />
                      </IconButton>
                    )}
                    {reminder.status === 'sent' && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleAcknowledgeReminder(reminder.id)}
                      >
                        <CheckCircle />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Reminder Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Reminder Details
          {selectedReminder && (
            <Typography variant="body2" color="text.secondary">
              {selectedReminder.reminderType.replace('_', ' ').toUpperCase()}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedReminder && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Reminder Information</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Chip
                      label={selectedReminder.reminderType.replace('_', ' ')}
                      color={getTypeColor(selectedReminder.reminderType) as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={selectedReminder.status}
                      color={getStatusColor(selectedReminder.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1">
                      {selectedReminder.dueDate.toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Amount</Typography>
                    <Typography variant="body1">
                      {selectedReminder.amount > 0 ? formatCurrency(selectedReminder.amount) : 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Customer & Loan</Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Customer</Typography>
                    <Typography variant="body1">
                      {getCustomerName(selectedReminder.customerId)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Loan</Typography>
                    <Typography variant="body1">
                      {getLoanNumber(selectedReminder.loanId)}
                    </Typography>
                  </Box>
                  {selectedReminder.sentDate && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Sent Date</Typography>
                      <Typography variant="body1">
                        {selectedReminder.sentDate.toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                  {selectedReminder.acknowledgedDate && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Acknowledged Date</Typography>
                      <Typography variant="body1">
                        {selectedReminder.acknowledgedDate.toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>Message</Typography>
                  <Typography variant="body1">
                    {selectedReminder.message}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {selectedReminder?.status === 'pending' && (
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={() => {
                handleSendReminder(selectedReminder.id);
                handleCloseDialog();
              }}
            >
              Send Reminder
            </Button>
          )}
          {selectedReminder?.status === 'sent' && (
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={() => {
                handleAcknowledgeReminder(selectedReminder.id);
                handleCloseDialog();
              }}
            >
              Mark Acknowledged
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReminderSystem;
